import api from '../../api';

const DUMMY_MODE_FLAG = '__salesProjectMonitoringDummyData';

const parseJwtPayload = (token) => {
  try {
    if (!token || !String(token).includes('.')) {
      return {};
    }

    const payload = String(token).split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );

    return JSON.parse(json);
  } catch (error) {
    return {};
  }
};

const getStoredUser = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    const token = stored?.token || stored?.accessToken || localStorage.getItem('authToken');
    return {
      ...parseJwtPayload(token),
      ...stored,
    };
  } catch (error) {
    return {};
  }
};

const normalizeRoleName = (role) => String(role?.name || role?.role || role || '').toUpperCase();

const isAdminUser = (user) => {
  const identities = [
    user?.email,
    user?.username,
    user?.sub,
    user?.name,
    user?.preferred_username,
  ].map((value) => String(value || '').toLowerCase());
  const roles = [
    ...(Array.isArray(user?.roles) ? user.roles : []),
    ...(Array.isArray(user?.authorities) ? user.authorities : []),
  ];
  return identities.includes('admin@cbqa.com')
    || identities.includes('admin@cbqaglobal.com')
    || roles.some((role) => normalizeRoleName(role).includes('ADMIN'));
};

const buildDummyResponse = () => ({
  [DUMMY_MODE_FLAG]: true,
  salesContext: {
    salesOwnerId: '2',
    salesOwnerName: 'Helda Mutiara',
    scopeLabel: 'Portfolio Sales',
  },
  summary: {
    totalProject: 12,
    activeProject: 8,
    onTrack: 4,
    atRisk: 2,
    overdue: 1,
    completed: 4,
    readyToInvoice: 2,
    pendingAddendum: 1,
    needContract: 3,
    rejectedReview: 2,
    applicationReview: 1,
    auditInProgress: 6,
    auditCompleted: 4,
    portfolioValue: 1245000000,
    portfolioValueCount: 12,
  },
  priorityActions: [
    {
      code: 'NEED_CONTRACT',
      title: 'Need Contract',
      count: 3,
      description: 'Project sudah aktif, dokumen kontrak/PO belum lengkap.',
    },
    {
      code: 'REVIEW_REJECTED',
      title: 'Rejected Review',
      count: 2,
      description: 'Application review ditolak dan perlu diterima Sales.',
    },
    {
      code: 'READY_TO_INVOICE',
      title: 'Ready to Invoice',
      count: 2,
      description: 'Project selesai atau siap ditagih, invoice belum jalan.',
    },
    {
      code: 'CLIENT_FOLLOW_UP',
      title: 'Waiting Client',
      count: 4,
      description: 'Butuh klarifikasi, approval, dokumen, atau jadwal client.',
    },
    {
      code: 'DELIVERY_AT_RISK',
      title: 'Delivery Risk',
      count: 2,
      description: 'Progress atau deadline delivery perlu dipantau.',
    },
    {
      code: 'ADDENDUM_PENDING',
      title: 'Addendum Pending',
      count: 1,
      description: 'Perubahan scope/standard/nilai belum selesai.',
    },
  ],
  portfolio: {
    health: { ON_TRACK: 4, AT_RISK: 2, OVERDUE: 1, BLOCKED: 1, COMPLETED: 4 },
    billing: { NOT_STARTED: 3, DRAFT: 2, SENT: 3, PAID: 3, OVERDUE: 1 },
    deliveryStage: { Planning: 2, 'In Progress': 5, Review: 1, Blocked: 1, Completed: 4 },
    auditStage: {
      'Not Started': 1,
      'Application Review': 1,
      Scheduling: 1,
      Audit: 4,
      'Technical Review': 1,
      Certificate: 0,
      'Application Review Rejected': 2,
      Blocked: 1,
      Completed: 4,
    },
    clientJourney: {
      NOT_STARTED: 2,
      UPCOMING: 3,
      RUNNING: 5,
      WAITING_CLIENT: 4,
      COMPLETED: 4,
    },
    clientMatrix: [
      {
        departmentId: 'business-assurance',
        departmentName: 'Business Assurance',
        totalProject: 7,
        newClient: { total: 2, active: 2, needContract: 1, atRisk: 1 },
        existingClient: { total: 3, active: 2, needContract: 1, atRisk: 0 },
        priorityClient: { total: 2, active: 2, needContract: 1, atRisk: 1 },
      },
      {
        departmentId: 'sustainability',
        departmentName: 'Sustainability',
        totalProject: 3,
        newClient: { total: 1, active: 1, needContract: 0, atRisk: 0 },
        existingClient: { total: 1, active: 1, needContract: 0, atRisk: 1 },
        priorityClient: { total: 1, active: 0, needContract: 0, atRisk: 0 },
      },
      {
        departmentId: 'training',
        departmentName: 'Training',
        totalProject: 2,
        newClient: { total: 1, active: 1, needContract: 1, atRisk: 0 },
        existingClient: { total: 1, active: 0, needContract: 0, atRisk: 0 },
        priorityClient: { total: 0, active: 0, needContract: 0, atRisk: 0 },
      },
    ],
  },
  contractWatchlist: [
    {
      projectId: 1,
      projectCode: 'PRJ-DUMMY-001',
      projectName: 'Sertifikasi ISO 9001',
      companyName: 'PT Contoh Sejahtera',
      contractStatus: 'NEED_CONTRACT',
      contractStatusLabel: 'Need Contract',
      nextAction: 'Minta PO atau kontrak final dari client.',
      severity: 'HIGH',
    },
    {
      projectId: 3,
      projectCode: 'PRJ-DUMMY-003',
      projectName: 'Surveillance ISCC',
      companyName: 'PT Sawit Makmur',
      contractStatus: 'DRAFT',
      contractStatusLabel: 'Draft Contract',
      nextAction: 'Follow-up tanda tangan kontrak.',
      severity: 'MEDIUM',
    },
  ],
  reviewRejected: [
    {
      id: 101,
      projectId: 4,
      projectCode: 'PRJ-DUMMY-004',
      projectName: 'Application Review ISO 27001',
      clientName: 'PT Digital Nusantara',
      rejectedBy: 'Application Reviewer',
      rejectedDate: '2026-07-22T09:30:00',
      reason: 'Scope belum jelas, Sales perlu konfirmasi kebutuhan client.',
      statusLabel: 'Need Sales Acceptance',
    },
    {
      id: 102,
      projectId: 5,
      projectCode: 'PRJ-DUMMY-005',
      projectName: 'Application Review SMK3',
      clientName: 'PT Karya Energi',
      rejectedBy: 'Technical Reviewer',
      rejectedDate: '2026-07-21T15:10:00',
      reason: 'Dokumen pendukung client belum lengkap.',
      statusLabel: 'Need Follow-up',
    },
  ],
  projects: {
    content: [
      {
        id: 1,
        projectCode: 'PRJ-DUMMY-001',
        projectName: 'Sertifikasi ISO 9001',
        companyName: 'PT Contoh Sejahtera',
        clientType: 'NEW',
        clientTypeLabel: 'New',
        departmentId: 'business-assurance',
        departmentName: 'Business Assurance',
        salesOwner: 'Budi Santoso',
        deliveryPic: 'Ani Wijaya',
        serviceNames: 'Certification',
        projectStatus: 'Ongoing',
        deliveryStage: 'In Progress',
        auditStage: 'Audit',
        auditStageLabel: 'Audit',
        lifecycle: 'RUNNING',
        lifecycleLabel: 'In progress',
        progressPercent: 55,
        startDate: '2026-06-01',
        targetEndDate: '2026-08-15',
        deadlineStatus: 'DUE_SOON',
        deadlineStatusLabel: 'Segera Jatuh Tempo',
        invoiceStatus: 'DRAFT',
        invoiceStatusLabel: 'Draft Invoice',
        contractStatus: 'NEED_CONTRACT',
        contractStatusLabel: 'Need Contract',
        lastUpdate: '2026-07-20T09:00:00',
        nextAction: 'Minta kontrak final dan pantau progres delivery.',
        health: 'AT_RISK',
        healthLabel: 'Butuh Follow-up',
        hasAddendum: false,
        readyToInvoice: false,
        needContract: true,
        reviewRejected: false,
        waitingClientClarification: false,
        documentGap: false,
        grandTotal: 45000000,
      },
      {
        id: 2,
        projectCode: 'PRJ-DUMMY-002',
        projectName: 'Audit ISCC',
        companyName: 'PT Sawit Makmur',
        clientType: 'PRIORITY',
        clientTypeLabel: 'Priority',
        departmentId: 'sustainability',
        departmentName: 'Sustainability',
        salesOwner: 'Siti Rahma',
        deliveryPic: 'Dedi Kurnia',
        serviceNames: 'Sustainability',
        projectStatus: 'Completed',
        deliveryStage: 'Completed',
        auditStage: 'Completed',
        auditStageLabel: 'Completed',
        lifecycle: 'COMPLETED',
        lifecycleLabel: 'Completed',
        progressPercent: 100,
        startDate: '2026-04-01',
        targetEndDate: '2026-06-01',
        deadlineStatus: 'COMPLETED',
        deadlineStatusLabel: 'Completed',
        invoiceStatus: 'NOT_STARTED',
        invoiceStatusLabel: 'Belum Ada Invoice',
        contractStatus: 'SIGNED',
        contractStatusLabel: 'Signed',
        lastUpdate: '2026-06-05T10:00:00',
        nextAction: 'Siapkan dan kirim invoice.',
        health: 'COMPLETED',
        healthLabel: 'Completed',
        hasAddendum: false,
        readyToInvoice: true,
        needContract: false,
        reviewRejected: false,
        waitingClientClarification: false,
        documentGap: false,
        grandTotal: 32000000,
      },
      {
        id: 4,
        projectCode: 'PRJ-DUMMY-004',
        projectName: 'Application Review ISO 27001',
        companyName: 'PT Digital Nusantara',
        clientType: 'EXISTING',
        clientTypeLabel: 'Existing',
        departmentId: 'business-assurance',
        departmentName: 'Business Assurance',
        salesOwner: 'Helda Mutiara',
        deliveryPic: 'Karunia Nurhayati',
        serviceNames: 'ISO/IEC 27001',
        projectStatus: 'Pending Review',
        deliveryStage: 'Planning',
        auditStage: 'Application Review Rejected',
        auditStageLabel: 'Application Review Rejected',
        lifecycle: 'WAITING_CLIENT',
        lifecycleLabel: 'Waiting for client',
        progressPercent: 10,
        startDate: '2026-07-10',
        targetEndDate: '2026-09-15',
        deadlineStatus: 'ON_SCHEDULE',
        deadlineStatusLabel: 'On Schedule',
        invoiceStatus: 'NOT_STARTED',
        invoiceStatusLabel: 'Belum Ada Invoice',
        contractStatus: 'SENT',
        contractStatusLabel: 'Sent',
        lastUpdate: '2026-07-22T09:30:00',
        nextAction: 'Terima rejected review dan konfirmasi scope ke client.',
        health: 'AT_RISK',
        healthLabel: 'Butuh Follow-up',
        hasAddendum: false,
        readyToInvoice: false,
        needContract: false,
        reviewRejected: true,
        waitingClientClarification: true,
        documentGap: true,
        grandTotal: 78000000,
      },
    ],
    totalElements: 3,
    totalPages: 1,
    page: 0,
    size: 10,
  },
  attentionNeeded: [
    {
      projectId: 4,
      projectCode: 'PRJ-DUMMY-004',
      projectName: 'Application Review ISO 27001',
      companyName: 'PT Digital Nusantara',
      reasonCode: 'REVIEW_REJECTED',
      reasonLabel: 'Review Rejected',
      detail: 'Sales perlu menerima reject dan follow-up scope ke client.',
    },
    {
      projectId: 2,
      projectCode: 'PRJ-DUMMY-002',
      projectName: 'Audit ISCC',
      companyName: 'PT Sawit Makmur',
      reasonCode: 'READY_TO_INVOICE',
      reasonLabel: 'Siap Ditagih',
      detail: 'Project selesai, invoice belum dikirim.',
    },
    {
      projectId: 1,
      projectCode: 'PRJ-DUMMY-001',
      projectName: 'Sertifikasi ISO 9001',
      companyName: 'PT Contoh Sejahtera',
      reasonCode: 'NEED_CONTRACT',
      reasonLabel: 'Need Contract',
      detail: 'Project aktif, kontrak final belum lengkap.',
    },
  ],
  filterOptions: {
    salesOwners: [
      { value: '1', label: 'Budi Santoso' },
      { value: '2', label: 'Siti Rahma' },
      { value: '3', label: 'Helda Mutiara' },
    ],
    clients: [
      { value: '1', label: 'PT Contoh Sejahtera' },
      { value: '2', label: 'PT Sawit Makmur' },
      { value: '4', label: 'PT Digital Nusantara' },
    ],
    departments: [
      { value: 'business-assurance', label: 'Business Assurance' },
      { value: 'sustainability', label: 'Sustainability' },
      { value: 'training', label: 'Training' },
    ],
    clientTypes: [
      { value: 'NEW', label: 'New' },
      { value: 'EXISTING', label: 'Existing' },
      { value: 'PRIORITY', label: 'Priority' },
    ],
    lifecycleStatuses: [
      { value: 'NOT_STARTED', label: 'Not started' },
      { value: 'UPCOMING', label: 'Upcoming' },
      { value: 'RUNNING', label: 'In progress' },
      { value: 'WAITING_CLIENT', label: 'Waiting for client' },
      { value: 'COMPLETED', label: 'Completed' },
    ],
    services: [
      { value: 'Certification', label: 'Certification' },
      { value: 'Sustainability', label: 'Sustainability' },
      { value: 'ISO/IEC 27001', label: 'ISO/IEC 27001' },
    ],
    projectStatuses: [
      { value: 'Ongoing', label: 'Ongoing' },
      { value: 'Pending Review', label: 'Pending Review' },
      { value: 'Completed', label: 'Completed' },
    ],
    healths: [
      { value: 'ON_TRACK', label: 'Sesuai Rencana' },
      { value: 'AT_RISK', label: 'Butuh Follow-up' },
      { value: 'OVERDUE', label: 'Terlambat' },
      { value: 'BLOCKED', label: 'Terblokir' },
      { value: 'COMPLETED', label: 'Completed' },
    ],
    auditStages: [
      { value: 'Not Started', label: 'Not Started' },
      { value: 'Application Review', label: 'Application Review' },
      { value: 'Scheduling', label: 'Scheduling' },
      { value: 'Audit', label: 'Audit' },
      { value: 'Technical Review', label: 'Technical Review' },
      { value: 'Certificate', label: 'Certificate' },
      { value: 'Application Review Rejected', label: 'Application Review Rejected' },
      { value: 'Blocked', label: 'Blocked' },
      { value: 'Completed', label: 'Completed' },
    ],
    invoiceStatuses: [
      { value: 'NOT_STARTED', label: 'Belum Ada Invoice' },
      { value: 'DRAFT', label: 'Draft Invoice' },
      { value: 'SENT', label: 'Terkirim' },
      { value: 'PAID', label: 'Lunas' },
      { value: 'OVERDUE', label: 'Terlambat' },
    ],
    contractStatuses: [
      { value: 'NEED_CONTRACT', label: 'Need Contract' },
      { value: 'DRAFT', label: 'Draft' },
      { value: 'SENT', label: 'Sent' },
      { value: 'SIGNED', label: 'Signed' },
      { value: 'EXPIRED', label: 'Expired' },
    ],
  },
});

const toFilterParam = (v) => (v == null || v === '' ? undefined : v);

export const isDummyMonitoringData = (data) => Boolean(data && data[DUMMY_MODE_FLAG]);

export const fetchSalesProjectMonitoring = async (filters = {}, page = 0, size = 10) => {
  const currentUser = getStoredUser();
  const adminScope = isAdminUser(currentUser);
  const params = {
    search: toFilterParam(filters.search),
    salesOwnerId: toFilterParam(filters.salesOwnerId),
    scope: toFilterParam(adminScope ? (filters.scope || 'all') : filters.scope),
    companyId: toFilterParam(filters.companyId),
    departmentId: toFilterParam(filters.departmentId),
    clientType: toFilterParam(filters.clientType),
    lifecycle: toFilterParam(filters.lifecycle),
    service: toFilterParam(filters.service),
    projectStatus: toFilterParam(filters.projectStatus),
    health: toFilterParam(filters.health),
    auditStage: toFilterParam(filters.auditStage),
    invoiceStatus: toFilterParam(filters.invoiceStatus),
    contractStatus: toFilterParam(filters.contractStatus),
    dateFrom: toFilterParam(filters.dateFrom),
    dateTo: toFilterParam(filters.dateTo),
    hasAddendum: toFilterParam(filters.hasAddendum),
    hasRejectedReview: toFilterParam(filters.hasRejectedReview),
    needContract: toFilterParam(filters.needContract),
    overdueOnly: toFilterParam(filters.overdueOnly),
    page,
    size,
  };

  try {
    const res = await api.get('/crm/sales/project-monitoring', { params });
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const isMissingEndpoint = status === 404 || !err?.response;
    if (isMissingEndpoint && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[SalesProjectMonitoring] Real endpoint unavailable, showing local dummy data (dev/staging only).',
        err,
      );
      return buildDummyResponse();
    }
    throw err;
  }
};
